<template>
    <BaseModal v-if="isVisible" @close="$emit('close')" class="container-modal">
        <template #header>
            <div class="quotes-header">{{ modalHeader }}</div>
        </template>

        <!-- AI-generated content -->
        <template v-if="mode === 'ai-generated'">
            <div class="quotes-summary">{{ summary }}</div>
            <div class="messages-info">Representative Comment Samples</div>
            <div class="messages-info-subtext">{{ toolTipText }}</div>
        </template>

        <!-- Raw feedback content -->
        <template v-else>
            <div class="raw-feedback-description">
                There were not enough responses to generate a summary. All feedback from this group
                is shared below.
            </div>
        </template>

        <div v-if="isLoading" class="loading-state">
            <BaseLoading />
        </div>

        <div v-else-if="messages.length === 0" class="empty-state">
            No feedback available for this group.
        </div>

        <div v-else class="quotes-list">
            <div class="quote" v-for="(message, index) in messages" :key="index">
                <p>"{{ message }}"</p>
            </div>
        </div>

        <template #footer>
            <ReportRequestDownloadButton
                class="report-download-button"
                reportType="CHATBOT_CONVERSATIONS"
                ariaLabel="Download Full Report"
                buttonText="Download Full Report"
                :reportOptions="{
                    chatbotFlowId,
                    cleanOutput: true
                }"
                @openModalReportDownload="$emit('close')"
            />
        </template>
    </BaseModal>
</template>

<script setup>
import { BaseModal, BaseLoading } from '@edsights/ui-core';
import ReportRequestDownloadButton from '@/components-deprecated/ReportRequestDownloadButton.vue';
import { computed, defineProps, defineEmits } from 'vue';
import { SVS_FEEDBACK_MODAL_MODE } from '@/consts/svs';

// Props
const props = defineProps({
    isVisible: {
        type: Boolean,
        default: false
    },
    messages: {
        type: Array,
        default: () => []
    },
    chatbotFlowId: {
        type: String,
        required: false
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    mode: {
        type: String,
        default: SVS_FEEDBACK_MODAL_MODE.RAW, // 'raw' or 'ai-generated'
        validator: (value) =>
            [SVS_FEEDBACK_MODAL_MODE.RAW, SVS_FEEDBACK_MODAL_MODE.AI_GENERATED].includes(value)
    },
    categoryName: {
        type: String,
        default: 'Raw Feedback'
    },
    summary: {
        type: String,
        default: ''
    }
});

// Emits
defineEmits(['close']);

// Computed
const modalHeader = computed(() => {
    return props.mode === 'ai-generated' ? props.categoryName : 'Raw Feedback';
});

const toolTipText =
    'The following comments are samples of feedback with occasional light edits for clarity and appropriateness while keeping the original intent intact.';
</script>

<style lang="scss" scoped>
@import '~@/styles/variables';

.container-modal {
    width: 40rem;
    font-size: 1.25rem;
}

.quotes-header {
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
}

.quotes-summary {
    font-size: 1.2rem;
    margin-bottom: 1rem;
}

.messages-info {
    display: flex;
    font-weight: bold;
    font-size: 1.2rem;
    padding: 30px 0px 0px 0px;
}

.messages-info-subtext {
    font-size: 12px;
    color: var(--color-gray-600);
    margin-bottom: 1rem;
}

.raw-feedback-description {
    color: var(--color-gray-600);
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 1.5rem;
}

.loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    color: var(--color-gray-600);
    font-style: italic;
}

.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem;
    color: var(--color-gray-500);
    font-style: italic;
}

.quotes-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-height: 400px;
    overflow-y: auto;
}

.quote {
    padding: 1.5rem;
    background: #f9f9f9;
    border-left: 5px solid #3498db;
    border-radius: 4px;
    position: relative;
}

.quote::before {
    font-size: 3rem;
    color: #3498db;
    position: absolute;
    top: -20px;
    left: 10px;
}

.quote p {
    margin: 0;
    font-size: 1.2rem;
    line-height: 1.5;
    color: #555;
}

.report-download-button {
    margin: 0.5rem 0;
}
</style>
